import React, { useState } from 'react';
import { hooks as commonHooks } from '@sitebuilder/common';
import { types as coreTypes } from '@sitebuilder/core';

import {
    TextInput,
    SideBarAccordion,
    SpinnerIcon,
    ErrorMessage,
    PrimaryButton,
    SuccessMessage,
} from './index';

export type DataKeys = 'id' | 'name' | 'description';

export type FormData =
    | coreTypes.SaveComponentFormData
    | coreTypes.SavePageFormData
    | coreTypes.SaveNewLocale;

interface AddNewItemFormProps {
    formTitle: string;
    data: FormData;
    dataError: FormData;
    showDescription: boolean;
    handleInputChange: (e: React.ChangeEvent<HTMLInputElement>, field: DataKeys) => void;
    handleSelectTemplate?: (template: string) => void;
    handleSubmit: () => void;
    saveError: string;
    saveSuccess: string;
    showSaveLoading: boolean;
    templates?: string[];
    showName?: boolean;
    formInputsLabel?: Record<string, string>;
    defaultActiveAccordionKey?: string;
}

const AddNewItemForm = (props: AddNewItemFormProps) => {
    const {
        data,
        dataError,
        showDescription,
        templates,
        showName,
        formInputsLabel,
        defaultActiveAccordionKey,
        handleInputChange,
        handleSelectTemplate,
        handleSubmit,
        ...rest
    } = props;
    const i18n = commonHooks.useLocalization();

    const [accordionActiveKeys, setAccordionActiveKeys] = useState(['']);

    const onClickAccordion = (eventKey: string) => {
        if (accordionActiveKeys.includes(eventKey)) {
            setAccordionActiveKeys(accordionActiveKeys.filter((k) => k !== eventKey));
        } else if (eventKey) {
            setAccordionActiveKeys([eventKey]);
        }
    };

    return (
        <>
            <hr className="divider" />
            <SideBarAccordion
                toggleTitle={rest.formTitle}
                eventKey="0"
                defaultActiveKey={defaultActiveAccordionKey || ''}
                accordionActiveKey={accordionActiveKeys}
                onClickAccordion={onClickAccordion}
            >
                <div className="save-form">
                    <TextInput
                        className={'form-input'}
                        label={formInputsLabel?.id || i18n.t('form_id')}
                        inputId={'id-input'}
                        labelId={'id-label'}
                        placeholder={formInputsLabel?.id || i18n.t('form_id')}
                        value={data.id}
                        onChange={(e) => handleInputChange(e, 'id')}
                        error={dataError.id}
                    />
                    {(showName === undefined || showName) && (
                        <TextInput
                            className={'form-input'}
                            label={formInputsLabel?.name || i18n.t('form_name')}
                            inputId={'name-input'}
                            placeholder={formInputsLabel?.name || i18n.t('form_name')}
                            value={data?.name || ''}
                            onChange={(e) => handleInputChange(e, 'name')}
                            error={dataError?.name}
                        />
                    )}
                    {showDescription && (
                        <TextInput
                            className={'form-input'}
                            label={formInputsLabel?.description || i18n.t('form_description')}
                            inputId={'description-input'}
                            placeholder={formInputsLabel?.description || i18n.t('form_description')}
                            value={data?.description || ''}
                            onChange={(e) => handleInputChange(e, 'description')}
                            error={dataError?.description}
                        />
                    )}
                    {templates && (
                        <>
                            <label className="select-label">Select Template</label>
                            <br />
                            <select
                                id="templateSelect"
                                className="custom-select-input"
                                data-testid="select"
                                onChange={(e) =>
                                    handleSelectTemplate &&
                                    handleSelectTemplate(e.currentTarget.value)
                                }
                            >
                                <option key="none" id="none" value={undefined}>
                                    None
                                </option>
                                {templates.map((t: any) => (
                                    <option data-testid="select-option" key={t} id={t} value={t}>
                                        {t}
                                    </option>
                                ))}
                            </select>
                        </>
                    )}
                    <div className="save">
                        {rest.saveError && <ErrorMessage error={rest.saveError} />}
                        {rest.saveSuccess && <SuccessMessage message={rest.saveSuccess} />}
                        <PrimaryButton
                            id={'save-button'}
                            className={'save-button'}
                            onClick={handleSubmit}
                        >
                            {rest.showSaveLoading && <SpinnerIcon />}
                            {i18n.t('save')}
                        </PrimaryButton>
                    </div>
                </div>
            </SideBarAccordion>
        </>
    );
};

export default AddNewItemForm;
